import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtDate, SLOTS } from "@/lib/format";
import { Button } from "@/components/ui/button";

const PALETTE = ["#0B5FFF", "#D97706", "#059669", "#DC2626", "#7C3AED", "#0891B2", "#DB2777", "#65A30D", "#475569"];
const ITALY = [42.5, 12.5];
const OPEN = "RECEIVED,TO_PLAN,PLANNED,IN_PROGRESS,SUSPENDED";

const FitBounds = ({ points }) => {
  const map = useMap();
  useMemo(() => { if (points.length) map.fitBounds(points.map((p) => [p.geo.lat, p.geo.lng]), { padding: [30, 30], maxZoom: 13 }); }, [points.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

export const OrdersMap = ({ params, onPick }) => {
  const { nameOf, visibleCompanies } = useData();
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);
  const [scope, setScope] = useState("open");
  const key = JSON.stringify(params);
  useEffect(() => {
    api.get("/orders", { params: { ...params, status: scope === "open" ? OPEN : "TO_PLAN,SUSPENDED", limit: 2000 } }).then((r) => setOrders(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [key, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  const color = useMemo(() => Object.fromEntries(visibleCompanies.map((c, i) => [c.id, PALETTE[i % PALETTE.length]])), [visibleCompanies]);
  const located = orders.filter((o) => o.geo?.lat);
  const missing = orders.filter((o) => !o.geo?.lat && !o.geo?.failed).length;
  const failed = orders.filter((o) => o.geo?.failed).length;
  const used = [...new Set(located.map((o) => o.executing_company_id))];
  const geocode = async () => {
    setBusy(true);
    try { const r = await api.post("/orders/geocode-missing"); toast.success(`${r.data.queued} indirizzi in posizionamento: ricarica tra qualche minuto (circa 1 al secondo)`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="orders-map">
      <div className="mb-2 flex flex-wrap items-center gap-3 text-sm">
        <div className="flex border"><Button size="sm" variant={scope === "open" ? "default" : "ghost"} onClick={() => setScope("open")} data-testid="orders-map-scope-open">Tutti gli ordini aperti</Button>
          <Button size="sm" variant={scope === "toplan" ? "default" : "ghost"} onClick={() => setScope("toplan")} data-testid="orders-map-scope-toplan">Solo da pianificare</Button></div>
        <span data-testid="orders-map-count"><b>{located.length}</b> ordini sulla mappa{missing > 0 && ` · ${missing} da posizionare`}{failed > 0 && ` · ${failed} indirizzi non trovati`}</span>
        {used.map((cid) => <span key={cid} className="flex items-center gap-1 text-xs"><span className="h-3 w-3 rounded-full" style={{ background: color[cid] || "#475569" }} />{nameOf("companies", cid, "code")}</span>)}
        {missing > 0 && <Button size="sm" variant="outline" className="ml-auto" onClick={geocode} disabled={busy} data-testid="orders-map-geocode"><MapPin className="mr-1 h-4 w-4" />Posiziona indirizzi mancanti</Button>}
      </div>
      <div className="h-[560px] border" data-testid="orders-map-canvas">
        <MapContainer center={ITALY} zoom={6} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={located} />
          {located.map((o) => (
            <CircleMarker key={o.id} center={[o.geo.lat, o.geo.lng]} radius={o.status === "TO_PLAN" || o.status === "RECEIVED" ? 8 : 6}
              pathOptions={{ color: "#0B0F14", weight: 1, fillColor: color[o.executing_company_id] || "#475569", fillOpacity: o.status === "TO_PLAN" || o.status === "RECEIVED" ? 0.9 : 0.4 }}>
              <Popup>
                <div className="text-xs" data-testid={`orders-map-popup-${o.id}`}>
                  <div className="font-mono text-sm font-bold">{o.external_order_id}</div>
                  <div>{o.customer_name}</div>
                  <div>{[[o.address?.street, o.address?.number].filter(Boolean).join(" "), o.address?.city].filter(Boolean).join(", ")}{o.address?.province && ` (${o.address.province})`}{o.geo.approx && " · posizione approssimata al comune"}</div>
                  <div className="mt-1">{nameOf("companies", o.executing_company_id, "code")} · {o.service_type}</div>
                  <div>{o.scheduled_date ? `Pianificato ${fmtDate(o.scheduled_date)} ${SLOTS[o.slot] || ""} · ${nameOf("teams", o.team_id)}` : "Da pianificare"}</div>
                  <button className="mt-2 font-semibold text-[#0B5FFF] underline" onClick={() => onPick(o)}>Pianifica</button>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Pallini pieni: ricevuti o da pianificare · chiari: già pianificati o in corso. Colore = azienda esecutrice. Le posizioni vengono calcolate dall'indirizzo all'importazione (OpenStreetMap).</p>
    </div>
  );
};
